import React, {
  useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState,
} from 'react';
import { createPortal } from 'react-dom';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import { ReactComponent as CheckmarkIcon } from 'assets/icons/check-mark.svg';
import { ReactComponent as ChevronIcon } from 'assets/icons/chevron.svg';
import { ReactComponent as CloseIcon } from 'assets/icons/close.svg';
import { ReactComponent as InfoIcon } from 'assets/icons/info.svg';
import { ReactComponent as ProductIcon } from 'assets/icons/product.svg';
import { ReactComponent as SearchIcon } from 'assets/icons/search.svg';
import { ReactComponent as WarehouseIcon } from 'assets/icons/warehouse.svg';
import clsx from 'clsx';
import Button from 'components/Button/Button';
import Modal from 'components/Modal/Modal';
import NumberField from 'components/NumberField/NumberField';
import StyledSelect from 'components/Select/Select';
import TextField from 'components/TextField/TextField';
import {
  AFFECTED_PERIOD, AFFECTED_PERIOD_UNIT, NAME, PRODUCTIVITY_IMPACT, UNIT_IDS,
} from 'constants/formFields';
import { DROPDOWN_ESTIMATED_HEIGHT, NO_LOCATION_KEY } from 'constants/general';
import { BUTTON_VARIANT, VALUE_CHAIN_UNIT_TYPES } from 'constants/interfaces';
import { compareCountryGroupKeys } from 'utils/country';
import { addScenarioFormSchema, type TAddScenarioFormSchema } from 'utils/validators';

import type { CSSProperties } from 'react';
import type { SingleValue } from 'react-select';
import type IAddScenarioSidebar from './interfaces/IAddScenarioSidebar';
import type { IScenarioFormSubmitData } from './interfaces/IAddScenarioSidebar';
import type IScenarioEntity from './interfaces/IScenarioEntity';
import type { TAddMode } from './interfaces/TAddMode';
import type { TPeriodUnitOption } from './interfaces/TPeriodUnitOption';

import styles from './sass/AddScenarioSidebar.module.scss';

function AddScenarioSidebar({
  boardEntities,
  isSaving,
  isEditing = false,
  initialData,
  onCancel,
  onSave,
  scrollContainerRef,
}: IAddScenarioSidebar): React.JSX.Element {
  const { t } = useTranslation();

  const PERIOD_UNIT_OPTIONS: TPeriodUnitOption[] = useMemo(() => [
    { value: 'day', label: t('valueChainMap.periodDays') },
    { value: 'week', label: t('valueChainMap.periodWeeks') },
    { value: 'month', label: t('valueChainMap.periodMonths') },
    { value: 'year', label: t('valueChainMap.periodYears') },
  ], [t]);

  const formDefaultValues = useMemo(() => {
    const periodUnitOption = PERIOD_UNIT_OPTIONS.find(
      (option) => option.value === initialData?.affectedPeriodUnit,
    ) ?? PERIOD_UNIT_OPTIONS[0];

    return {
      [NAME]: initialData?.name ?? '',
      [AFFECTED_PERIOD]: initialData?.affectedPeriod,
      [AFFECTED_PERIOD_UNIT]: periodUnitOption,
      [PRODUCTIVITY_IMPACT]: initialData?.productivityImpact,
      [UNIT_IDS]: initialData?.unitIds ?? [],
    };
  }, [PERIOD_UNIT_OPTIONS, initialData]);

  const getEntityIcon = (type: string): React.ReactNode => {
    if (type === VALUE_CHAIN_UNIT_TYPES.SUPPLIER) {
      return <WarehouseIcon />;
    }
    return <ProductIcon />;
  };

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isSubmitted },
  } = useForm<TAddScenarioFormSchema>({
    mode: 'onSubmit',
    resolver: yupResolver(addScenarioFormSchema),
    defaultValues: formDefaultValues,
  });

  const [selectedEntityIds, setSelectedEntityIds] = useState<Set<number>>(
    () => new Set(formDefaultValues[UNIT_IDS]),
  );
  const [addMode, setAddMode] = useState<TAddMode>(null);
  const [countrySearch, setCountrySearch] = useState('');
  const [entitySearch, setEntitySearch] = useState('');
  const [isEntitiesCollapsed, setIsEntitiesCollapsed] = useState(false);
  const [isCancelConfirmationOpen, setIsCancelConfirmationOpen] = useState(false);
  const [isResetAllConfirmOpen, setIsResetAllConfirmOpen] = useState(false);
  const [clearGroupKey, setClearGroupKey] = useState<string | null>(null);
  const entitiesSectionRef = useRef<HTMLDivElement>(null);
  const addModeControlsRef = useRef<HTMLDivElement>(null);
  const dropdownPortalRef = useRef<HTMLDivElement>(null);
  const [dropdownPanelStyle, setDropdownPanelStyle] = useState<CSSProperties | null>(null);

  const availableCountries = useMemo(() => {
    const countries = new Set<string>();
    boardEntities.forEach((entity) => {
      if (entity.country) {
        countries.add(entity.country);
      }
    });
    return Array.from(countries).sort();
  }, [boardEntities]);

  const filteredCountries = useMemo(() => {
    const q = countrySearch.trim().toLowerCase();
    if (!q) return availableCountries;
    return availableCountries.filter((c) => c.toLowerCase().includes(q));
  }, [availableCountries, countrySearch]);

  const filteredEntities = useMemo(() => {
    const q = entitySearch.trim().toLowerCase();
    if (!q) return boardEntities;
    return boardEntities.filter((e) => e.name.toLowerCase().includes(q));
  }, [boardEntities, entitySearch]);

  const selectedEntities = useMemo(
    () => boardEntities.filter((e) => selectedEntityIds.has(e.id)),
    [boardEntities, selectedEntityIds],
  );

  const selectedCountries = useMemo(() => {
    const countries = new Set<string>();
    boardEntities.forEach((entity) => {
      if (selectedEntityIds.has(entity.id) && entity.country) {
        countries.add(entity.country);
      }
    });
    return countries;
  }, [boardEntities, selectedEntityIds]);

  const groupedEntities = useMemo(() => {
    const groups = new Map<string, IScenarioEntity[]>();
    selectedEntities.forEach((entity) => {
      const key = entity.country || NO_LOCATION_KEY;
      const existing = groups.get(key) || [];
      existing.push(entity);
      groups.set(key, existing);
    });

    return Array.from(groups.entries()).sort(([leftGroupKey], [rightGroupKey]) => (
      compareCountryGroupKeys(leftGroupKey, rightGroupKey)
    ));
  }, [selectedEntities]);

  const resetScenarioFormState = useCallback(() => {
    reset(formDefaultValues);
    setSelectedEntityIds(new Set(formDefaultValues[UNIT_IDS]));
    setAddMode(null);
    setCountrySearch('');
    setEntitySearch('');
  }, [formDefaultValues, reset]);

  const handleAddByCountry = (country: string) => {
    const countryEntityIds = boardEntities
      .filter((e) => e.country === country)
      .map((e) => e.id);

    setSelectedEntityIds((prev) => {
      const next = new Set(prev);
      countryEntityIds.forEach((id) => next.add(id));
      return next;
    });
  };

  const handleToggleEntity = (entity: IScenarioEntity) => {
    setSelectedEntityIds((prev) => {
      const next = new Set(prev);
      if (next.has(entity.id)) {
        next.delete(entity.id);
      } else {
        next.add(entity.id);
      }
      return next;
    });
  };

  const handleRemoveEntity = (id: number) => {
    setSelectedEntityIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  const handleClearGroup = (groupKey: string) => {
    setClearGroupKey(groupKey);
  };

  const handleConfirmClearGroup = () => {
    if (clearGroupKey === null) return;
    const idsToRemove = new Set(
      boardEntities
        .filter((e) => (e.country || NO_LOCATION_KEY) === clearGroupKey)
        .map((e) => e.id),
    );
    setSelectedEntityIds((prev) => {
      const next = new Set(prev);
      idsToRemove.forEach((id) => next.delete(id));
      return next;
    });
    setClearGroupKey(null);
  };

  const handleResetAll = () => {
    setIsResetAllConfirmOpen(true);
  };

  const handleConfirmResetAll = () => {
    setSelectedEntityIds(new Set());
    setIsResetAllConfirmOpen(false);
    setAddMode(null);
  };

  const handleToggleAddMode = (mode: TAddMode) => {
    setAddMode((prev) => (prev === mode ? null : mode));
    setCountrySearch('');
    setEntitySearch('');
  };

  const handleCancel = () => {
    setIsCancelConfirmationOpen(true);
  };

  const handleRejectCancelConfirmation = () => {
    setIsCancelConfirmationOpen(false);
  };

  const handleConfirmCancel = () => {
    resetScenarioFormState();
    setIsCancelConfirmationOpen(false);
    onCancel();
  };

  const handleFormSubmit = async (formData: TAddScenarioFormSchema) => {
    const unitOption = formData[AFFECTED_PERIOD_UNIT] as TPeriodUnitOption;
    const submitData: IScenarioFormSubmitData = {
      name: formData[NAME] as string,
      affectedPeriod: formData[AFFECTED_PERIOD] as number,
      affectedPeriodUnit: unitOption.value,
      productivityImpact: formData[PRODUCTIVITY_IMPACT] as number,
      unitIds: formData[UNIT_IDS] as number[],
    };

    try {
      await onSave(submitData);
    } catch {
      return;
    }

    resetScenarioFormState();
  };

  const getGroupLabel = (key: string) => {
    if (key === NO_LOCATION_KEY) {
      return t('valueChainMap.noLocation');
    }
    return key;
  };

  const handleEntitiesHeaderKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setIsEntitiesCollapsed((prev) => !prev);
    }
  };

  useEffect(() => {
    setValue(UNIT_IDS, Array.from(selectedEntityIds), {
      shouldValidate: isSubmitted,
    });
  }, [isSubmitted, selectedEntityIds, setValue]);

  useEffect(() => {
    resetScenarioFormState();
  }, [resetScenarioFormState]);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      const target = event.target as Node;
      if (entitiesSectionRef.current?.contains(target)) return;
      if (dropdownPortalRef.current?.contains(target)) return;
      setAddMode(null);
    };

    document.addEventListener('mousedown', handleOutsideClick);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, []);

  useLayoutEffect(() => {
    if (!addMode) {
      setDropdownPanelStyle(null);
      return () => {};
    }

    const updatePosition = () => {
      const node = addModeControlsRef.current;
      if (!node) {
        setDropdownPanelStyle(null);
        return;
      }

      const r = node.getBoundingClientRect();
      const containerRect = scrollContainerRef?.current?.getBoundingClientRect();
      const containerTop = containerRect?.top ?? 0;
      const containerBottom = containerRect?.bottom ?? window.innerHeight;
      const spaceBelow = containerBottom - r.bottom;
      const spaceAbove = r.top - containerTop;
      const openUp = spaceBelow < DROPDOWN_ESTIMATED_HEIGHT && spaceAbove > spaceBelow;

      setDropdownPanelStyle({
        position: 'fixed',
        left: r.left,
        width: r.width,
        zIndex: 10000,
        ...(openUp
          ? { top: r.top - 8, transform: 'translateY(-100%)' }
          : { top: r.bottom + 8, transform: 'none' }),
      });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [addMode, scrollContainerRef]);

  return (
    <div className={styles.block}>
      <div className={styles.blockHeader}>
        <span className={styles.blockHeaderTitle}>
          {isEditing ? t('valueChainMap.editScenarioTitle') : t('valueChainMap.addScenarioTitle')}
        </span>
      </div>
      <form
        id="add-scenario-form"
        className={styles.formFields}
        onSubmit={handleSubmit(handleFormSubmit)}
        noValidate
      >
        <Controller
          control={control}
          name={NAME}
          render={({ field: { name, value, onChange } }) => (
            <TextField
              name={name}
              label={t('valueChainMap.scenarioNameLabel')}
              value={value ?? ''}
              onChange={(e) => onChange(e.target.value)}
              error={errors[NAME]?.message}
              maxLength={100}
              required
            />
          )}
        />

        <div className={styles.periodRow}>
          <Controller
            control={control}
            name={AFFECTED_PERIOD}
            render={({ field: { name, value, onChange } }) => (
              <NumberField
                name={name}
                label={t('valueChainMap.affectedPeriodLabel')}
                value={value != null ? String(value) : ''}
                onChange={(values) => {
                  if (values.floatValue == null) {
                    onChange(null);
                    return;
                  }

                  onChange(values.floatValue);
                }}
                error={errors[AFFECTED_PERIOD]?.message}
                allowNegative={false}
                required
                className={styles.periodField}
              />
            )}
          />
          <Controller
            control={control}
            name={AFFECTED_PERIOD_UNIT}
            render={({ field: { value, onChange } }) => (
              <StyledSelect
                name={AFFECTED_PERIOD_UNIT}
                options={PERIOD_UNIT_OPTIONS}
                value={value as SingleValue<TPeriodUnitOption> ?? PERIOD_UNIT_OPTIONS[0]}
                onChange={(option) => onChange(option)}
                className={styles.periodUnitSelect}
                menuPosition="absolute"
              />
            )}
          />
        </div>

        <Controller
          control={control}
          name={PRODUCTIVITY_IMPACT}
          render={({ field: { name, value, onChange } }) => (
            <NumberField
              name={name}
              label={t('valueChainMap.productivityImpactLabel')}
              value={value != null ? String(Math.abs(value)) : ''}
              onChange={(values) => {
                const nextValue = values.floatValue;

                if (nextValue == null) {
                  onChange(null);
                  return;
                }

                onChange(-Math.abs(nextValue));
              }}
              error={errors[PRODUCTIVITY_IMPACT]?.message}
              allowNegative={false}
              max={100}
              required
              icon={<span className={styles.minusPrefix}>-</span>}
            />
          )}
        />

        <div
          ref={entitiesSectionRef}
          className={clsx(styles.entitiesSection, {
            [styles.entitiesSectionError]: errors[UNIT_IDS]?.message,
          })}
          aria-invalid={Boolean(errors[UNIT_IDS]?.message)}
        >
          <div
            className={styles.entitiesSectionHeader}
            role="button"
            tabIndex={0}
            onClick={() => setIsEntitiesCollapsed((prev) => !prev)}
            onKeyDown={handleEntitiesHeaderKeyDown}
            aria-expanded={!isEntitiesCollapsed}
          >
            <span className={styles.entitiesSectionTitle}>
              <strong>{t('valueChainMap.affectedEntitiesLabel')}</strong>
              {' '}
              <span className={styles.entitiesCount}>
                (
                {selectedEntityIds.size}
                )
              </span>
            </span>
            <div className={styles.entitiesHeaderRight}>
              {selectedEntityIds.size > 0 ? (
                <div
                  role="presentation"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Button
                    title={t('valueChainMap.resetAll')}
                    variant={BUTTON_VARIANT.ghost}
                    onClick={handleResetAll}
                    className={styles.resetAllButton}
                  />
                </div>
              ) : null}
              <ChevronIcon
                className={clsx(styles.chevronIcon, {
                  [styles.collapsed]: isEntitiesCollapsed,
                })}
              />
            </div>
          </div>

          {!isEntitiesCollapsed ? (
            <div className={styles.entitiesBody}>
              <div ref={addModeControlsRef} className={styles.addModeControls}>
                <div className={styles.addModeButtons}>
                  <Button
                    title={t('valueChainMap.addByCountry')}
                    variant={BUTTON_VARIANT.cancel}
                    className={clsx(styles.addModeButton, {
                      [styles.active]: addMode === 'country',
                    })}
                    onClick={() => handleToggleAddMode('country')}
                  />
                  <Button
                    title={t('valueChainMap.addViaSearch')}
                    variant={BUTTON_VARIANT.cancel}
                    className={clsx(styles.addModeButton, {
                      [styles.active]: addMode === 'search',
                    })}
                    onClick={() => handleToggleAddMode('search')}
                  />
                </div>
              </div>

              {groupedEntities.length === 0 ? (
                <div className={styles.emptyEntities}>
                  <InfoIcon className={styles.emptyEntitiesIcon} aria-hidden="true" />
                  <p className={styles.emptyEntitiesText}>
                    {t('valueChainMap.noAffectedEntities')}
                  </p>
                </div>
              ) : (
                <div className={styles.groupsList}>
                  {groupedEntities.map(([groupKey, entities]) => (
                    <div key={groupKey} className={styles.group}>
                      <div className={styles.groupHeader}>
                        <span className={styles.groupTitle}>
                          {getGroupLabel(groupKey)}
                          {' '}
                          (
                          {entities.length}
                          ):
                        </span>
                        <Button
                          title={t('valueChainMap.clearGroup')}
                          variant={BUTTON_VARIANT.ghost}
                          className={styles.clearGroupButton}
                          onClick={() => handleClearGroup(groupKey)}
                        />
                      </div>
                      <div className={styles.groupItems}>
                        {entities.map((entity) => (
                          <div
                            key={entity.id}
                            className={clsx(styles.entityPill, {
                              [styles.entityPillProduct]: entity.type !== VALUE_CHAIN_UNIT_TYPES.SUPPLIER,
                            })}
                          >
                            <span className={styles.entityPillIcon}>
                              {getEntityIcon(entity.type)}
                            </span>
                            <span className={styles.entityPillName}>{entity.name}</span>
                            <Button
                              title=""
                              variant={BUTTON_VARIANT.ghost}
                              className={styles.entityPillRemove}
                              startIcon={<CloseIcon />}
                              onClick={() => handleRemoveEntity(entity.id)}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : null}
        </div>
        {errors[UNIT_IDS]?.message ? (
          <p className={styles.fieldError}>{errors[UNIT_IDS]?.message as string}</p>
        ) : null}
      </form>

      <div className={styles.formFooter}>
        <Button
          title={t('common.cancel')}
          variant={BUTTON_VARIANT.cancel}
          onClick={handleCancel}
          className={styles.cancelButton}
        />
        <Button
          title={t('valueChainMap.saveScenario')}
          variant={BUTTON_VARIANT.primary}
          isLoading={isSaving}
          disabled={isSaving}
          form="add-scenario-form"
          type="submit"
          className={styles.saveButton}
        />
      </div>

      {addMode && dropdownPanelStyle
        ? createPortal(
          <div
            ref={dropdownPortalRef}
            className={styles.dropdownPanel}
            style={dropdownPanelStyle}
          >
            {addMode === 'country' ? (
              <>
                <TextField
                  name="countrySearch"
                  value={countrySearch}
                  onChange={(e) => setCountrySearch(e.target.value)}
                  placeholder={`${t('valueChainMap.searchByName')}...`}
                  icon={<SearchIcon />}
                  className={styles.dropdownSearch}
                />
                <div className={styles.dropdownList}>
                  {filteredCountries.length === 0 ? (
                    <div className={styles.emptyDropdown}>
                      {t('valueChainMap.noCountriesFound')}
                    </div>
                  ) : (
                    filteredCountries.map((country) => (
                      <div
                        key={country}
                        className={clsx(styles.dropdownItem, {
                          [styles.selected]: selectedCountries.has(country),
                        })}
                        role="button"
                        tabIndex={0}
                        onClick={() => handleAddByCountry(country)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            handleAddByCountry(country);
                          }
                        }}
                      >
                        <span>{country}</span>
                        {selectedCountries.has(country) ? (
                          <CheckmarkIcon className={styles.dropdownItemCheck} />
                        ) : null}
                      </div>
                    ))
                  )}
                </div>
              </>
            ) : null}
            {addMode === 'search' ? (
              <>
                <TextField
                  name="entitySearch"
                  value={entitySearch}
                  onChange={(e) => setEntitySearch(e.target.value)}
                  placeholder={`${t('valueChainMap.searchByName')}...`}
                  icon={<SearchIcon />}
                  className={styles.dropdownSearch}
                />
                <div className={styles.dropdownList}>
                  {filteredEntities.length === 0 ? (
                    <div className={styles.emptyDropdown}>
                      {t('valueChainMap.noAffectedEntitiesFound')}
                    </div>
                  ) : (
                    filteredEntities.map((entity) => (
                      <div
                        key={entity.id}
                        className={clsx(styles.dropdownItem, {
                          [styles.selected]: selectedEntityIds.has(entity.id),
                        })}
                        role="button"
                        tabIndex={0}
                        onClick={() => handleToggleEntity(entity)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            handleToggleEntity(entity);
                          }
                        }}
                      >
                        <span>{entity.name}</span>
                        {selectedEntityIds.has(entity.id) ? (
                          <CheckmarkIcon className={styles.dropdownItemCheck} />
                        ) : null}
                      </div>
                    ))
                  )}
                </div>
              </>
            ) : null}
          </div>,
          document.body,
        )
        : null}

      <Modal
        open={isCancelConfirmationOpen}
        title={t('clients.discardChanges')}
        description={t('clients.informationWillBeLost')}
        closeTitle={t('common.keepEditing')}
        confirmTitle={t('common.discardChanges')}
        confirmVariant={BUTTON_VARIANT.error}
        onClose={handleRejectCancelConfirmation}
        onReject={handleRejectCancelConfirmation}
        onConfirm={handleConfirmCancel}
      />

      <Modal
        open={isResetAllConfirmOpen}
        title={t('valueChainMap.resetAllConfirmTitle')}
        description={t('valueChainMap.resetAllConfirmDescription')}
        closeTitle={t('common.cancel')}
        confirmTitle={t('valueChainMap.resetAll')}
        confirmVariant={BUTTON_VARIANT.error}
        onClose={() => setIsResetAllConfirmOpen(false)}
        onReject={() => setIsResetAllConfirmOpen(false)}
        onConfirm={handleConfirmResetAll}
      />

      <Modal
        open={clearGroupKey !== null}
        title={t('valueChainMap.clearGroupConfirmTitle')}
        description={t('valueChainMap.clearGroupConfirmDescription')}
        closeTitle={t('common.cancel')}
        confirmTitle={t('valueChainMap.clearGroup')}
        confirmVariant={BUTTON_VARIANT.error}
        onClose={() => setClearGroupKey(null)}
        onReject={() => setClearGroupKey(null)}
        onConfirm={handleConfirmClearGroup}
      />
    </div>
  );
}

export default AddScenarioSidebar;
